import { createSearchParams, type URLSearchParamsInit } from "react-router-dom";
import { findBy, keyById, unreachableCaseError } from "@edgetier/utilities";
import type { IFilter } from "./filters.types";

/**
 * Build the URL search parameters for a filter form submission. The submitted values replace the current parameters,
 * except for the preserved ones, which are carried over so that view state the form doesn't own (for example a
 * comparison toggle or a table's selection) survives a filter change.
 * @param {URLSearchParams} currentSearch - The search parameters currently in the URL.
 * @param {object} submittedValues - The transformed form values being synced to the URL.
 * @param {string[]} preservedParameters - Exact parameter names or `<entry>.` namespaces to carry over.
 * @returns {URLSearchParams} The search parameters to navigate to.
 */
export const mergePreservedParameters = (
    currentSearch: URLSearchParams,
    submittedValues: URLSearchParamsInit,
    preservedParameters: readonly string[]
): URLSearchParams => {
    const nextSearch = createSearchParams(submittedValues);

    if (preservedParameters.length === 0) {
        return nextSearch;
    }

    // Snapshot the submitted keys up front so every value of a repeated parameter is carried over, and so a parameter
    // the form owns always wins over the value already in the URL.
    const submittedKeys = new Set(nextSearch.keys());

    currentSearch.forEach((value, key) => {
        const isPreserved = preservedParameters.some(
            (parameter) => key === parameter || key.startsWith(`${parameter}.`)
        );

        if (isPreserved && !submittedKeys.has(key)) {
            nextSearch.append(key, value);
        }
    });

    return nextSearch;
};

/**
 * Function to get the filter name based on the filter type.
 * @param {object} filter - The filter configuration.
 * @returns {string} The filter name.
 */
export const getFilterName = (filter: IFilter): string => {
    switch (filter.type) {
        case "date-range":
            return filter.startDateName;
        case "select":
        case "search":
        case "select-enum":
        case "custom":
            return filter.name;
        default:
            unreachableCaseError(filter);
            return "unknown";
    }
};

/**
 * Function to get the filter components that are not visible based on the visible fields.
 * @param {object[]} filterComponents - All filter components.
 * @param {object[]} visibleFields - Visible filter components id's.
 * @returns {object[]} Filter components that are not visible.
 */
export const getNotVisibleFields = (filterComponents: IFilter[], visibleFields: string[]) => {
    return filterComponents.filter((filter) => !visibleFields.includes(filter.id));
};

/**
 * Function to get the visible fields based on their IDs from a list of filters.
 * @param {string[]} visibleFilterIds - The IDs of the visible filters.
 * @param {object[]} visibleFields - The list of visible filter configurations.
 * @returns {object[]} Visible filter fields.
 */
export const getVisibleFields = (visibleFilterIds: string[], visibleFields: IFilter[]) => {
    const parsedFields = fieldsById(visibleFields);
    return visibleFilterIds.map((id) => parsedFields[id]).filter((filter) => typeof filter !== "undefined");
};

/**
 * Function to create a dictionary of filters indexed by their IDs.
 * @param {object[]} filters - The list of filter configurations.
 * @returns {object} Dictionary of filters indexed by their IDs.
 */
export const fieldsById = (filters: IFilter[]): Record<string, IFilter> => {
    return keyById(filters, (filter) => filter.id);
};

/**
 * Function to get a filter by its ID from a list of filters.
 * @param {object[]} filters - The list of filters.
 * @param {string} id - The ID of the filter to find.
 * @returns {object | undefined} The filter with the specified ID, or undefined if not found.
 */
export const getFieldById = (filters: IFilter[], id: string) => {
    return findBy(filters, (filter) => filter.id, id);
};
